import { useMemo, useState } from 'react';

import { Empty, Typography } from 'antd';

import { useDebounce } from '@/shared/hooks/useDebounce';
import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import { EMPTY_MESSAGES, VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { sanitizeToText } from '@/shared/lib/sanitize';
import { getTransformSortByToSortOption } from '@/shared/lib/sort/transformSortBy';
import { SearchInput } from '@/shared/ui/SearchInput';

import { useSearchStore } from '@/entities/search';

import { useCertificationAssets } from '@/features/tags/model';

import { SearchResults } from '@/widgets/search';

import styles from './TagAssetList.module.css';

const { Text } = Typography;

type TagAssetListProps = {
  tagFqn?: string;
};

const TagAssetList = ({ tagFqn }: TagAssetListProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const sanitizedQuery = sanitizeToText(debouncedQuery);

  const { validateLength, handleCompositionStart, handleCompositionEnd } = useSearchLengthValidation({
    maxLength: 30,
    warningMessage: VALIDATION_MESSAGES.TAG_SEARCH_MAX_LENGTH,
    debounceMs: 1000,
  });

  const emptyFilters = useMemo(
    () => ({
      dataSource: [],
      assetType: [],
      domain: [],
      owner: [],
      tier: [],
      tags: [],
      certification: [],
    }),
    []
  );

  const { sortBy } = useSearchStore();
  const transformedSort = getTransformSortByToSortOption(sortBy);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isError, error } = useCertificationAssets({
    certificationTagFqn: tagFqn ?? '',
    filters: emptyFilters,
    query: sanitizedQuery,
    enabled: !!tagFqn,
    sortBy: transformedSort.field,
    sortOrder: transformedSort.direction,
  });
  const { result, totalCount } = data ?? { result: [], totalCount: 0 };

  const emptyContent = useMemo(() => {
    if (isError) {
      return (
        <div className={styles.emptyState}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="danger">{error instanceof Error ? error.message : '오류가 발생했습니다'}</Text>}
          />
        </div>
      );
    }

    if ((result ?? []).length === 0) {
      const description = sanitizedQuery
        ? `"${sanitizedQuery}"에 대한 검색 결과가 없습니다`
        : EMPTY_MESSAGES.NO_CONNECTED_ASSETS;

      return (
        <div className={styles.emptyState}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} />
        </div>
      );
    }

    return null;
  }, [isError, error, result, sanitizedQuery]);

  return (
    <div className={styles.tagAssetList}>
      <div className={styles.contentArea}>
        <div className={styles.resultsWithPreview}>
          <div className={styles.resultsContainer}>
            <SearchResults
              results={result ?? []}
              query={sanitizedQuery}
              totalCount={totalCount}
              isFetching={isFetching}
              isLoading={isLoading}
              onLoadMore={() => hasNextPage && fetchNextPage()}
              hasMore={hasNextPage}
              headerRight={
                <SearchInput
                  placeholder="이 태그 내에서 검색"
                  value={searchQuery}
                  onChange={(e) => {
                    const validatedValue = validateLength(e.target.value);
                    setSearchQuery(validatedValue);
                  }}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                  maxLength={30}
                />
              }
              emptyContent={emptyContent}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export { TagAssetList };
